import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import AdminNavbar from "./AdminNavbar";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Admin CRUD for Instagram post URLs. Each URL is stored as-is in Mongo; the
 * public Social Feed component handles conversion to embed URLs on render.
 * Accepts the standard post URL format: https://www.instagram.com/p/<id>/
 * or a reel URL: https://www.instagram.com/reel/<id>/
 */
export default function InstagramDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/instagram-posts`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram-posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error();
      setUrl("");
      await load();
      Swal.fire({
        icon: "success",
        title: "Added.",
        confirmButtonColor: "#DC2626",
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed to add",
        confirmButtonColor: "#DC2626",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Remove this post?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#DC2626",
    });
    if (!ok.isConfirmed) return;

    try {
      const res = await fetch(`${API_URL}/api/instagram-posts/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      setItems(items.filter((i) => i._id !== id));
      Swal.fire({
        icon: "success",
        title: "Deleted.",
        confirmButtonColor: "#DC2626",
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        confirmButtonColor: "#DC2626",
      });
    }
  };

  return (
    <div className="admin-shell">
      <AdminNavbar />

      <div className="admin-page">
        <div className="admin-head">
          <div>
            <p className="eyebrow">Social</p>
            <h1>
              Manage <em>Instagram</em>.
            </h1>
          </div>
        </div>

        <form
          className="admin-form"
          onSubmit={handleAdd}
          style={{ marginBottom: "4rem" }}
        >
          <div className="field">
            <label htmlFor="url">Post URL</label>
            <input
              id="url"
              type="url"
              placeholder="https://www.instagram.com/p/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <small style={{ color: "var(--muted)", marginTop: "0.4rem" }}>
              Paste a public post or reel URL. The feed pulls in the embed
              automatically.
            </small>
          </div>
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? "Adding..." : "Add Post →"}
          </button>
        </form>

        {loading ? (
          <div className="loader-wrap">
            <div className="loader" />
          </div>
        ) : items.length === 0 ? (
          <p className="empty-state">No Instagram posts added yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>URL</th>
                <th>Added</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id}>
                  <td>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--accent)" }}
                    >
                      {p.url} ↗
                    </a>
                  </td>
                  <td>
                    {p.createdAt
                      ? new Date(p.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(p._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
